import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display"]
  static values = {
    startDate: String,
    endDate: String,
    allowPastDates: Boolean,
    unavailableDates: String
  }

  connect() {
    this.pickerReady = this.initPicker()
  }

  async initPicker() {
    // Lazy load flatpickr only when needed
    const flatpickr = await this.loadFlatpickr()

    const startDateStr = this.startDateValue
    const endDateStr = this.endDateValue

    // Parse dates from controller - they should always be provided
    // Use explicit parsing to avoid UTC vs local time issues
    const defaultStart = startDateStr ? this.parseLocalDate(startDateStr) : new Date()
    const defaultEnd = endDateStr ? this.parseLocalDate(endDateStr) : new Date()

    // Parse and store unavailable dates
    this.parseUnavailableDates()

    this.picker = flatpickr(this.displayTarget, {
      mode: "range",
      dateFormat: "d/m/Y",
      rangeSeparator: " – ",
      showMonths: this.getMonthsToShow(),
      allowInput: true,
      clickOpens: true,
      minDate: this.allowPastDatesValue ? null : "today",
      defaultDate: [defaultStart, defaultEnd],
      onDayCreate: (dObj, dStr, fp, dayElem) => {
        // Visually mark closed dates as disabled, but don't prevent range selection
        if (this.isClosedDate(dayElem.dateObj)) {
          dayElem.classList.add("flatpickr-disabled")
          dayElem.setAttribute("aria-disabled", "true")
          dayElem.setAttribute("title", this.closedDateTooltip(dayElem.dateObj))
        }
      },
      onChange: (selectedDates, dateStr, instance) => {
        // Validate that neither start nor end date is a closed date
        if (selectedDates.length > 0) {
          const hasClosedStartOrEnd = selectedDates.some(date => this.isClosedDate(date))

          if (hasClosedStartOrEnd) {
            // Clear the selection if a closed date was selected
            instance.clear()
            return
          }
        }

        this.updateHiddenFields(selectedDates)
      }
    })

    // Set initial values (without triggering search)
    this.updateHiddenFields([defaultStart, defaultEnd], { triggerSearch: false })
  }

  // Quick-range preset buttons: set the picker's dates without navigating.
  // Whether that submits anything is governed by data-datepicker-auto-submit,
  // exactly as if the user picked the dates in the calendar.
  async setRange(event) {
    await this.pickerReady
    this.picker.setDate(
      [this.parseLocalDate(event.params.start), this.parseLocalDate(event.params.end)],
      true
    )
  }

  async loadFlatpickr() {
    // Dynamic import - only loads when datepicker is used
    const module = await import("flatpickr")
    return module.default
  }

  updateHiddenFields(selectedDates, options = {}) {
    if (selectedDates.length === 2) {
      const arrivalDate = this.formatDate(selectedDates[0])
      const departureDate = this.formatDate(selectedDates[1])

      // Find and update the hidden fields directly
      const arrivalField = this.element.querySelector(`[data-search-form-target="arrivalDate"]`)
      const departureField = this.element.querySelector(`[data-search-form-target="departureDate"]`)

      if (arrivalField) {
        arrivalField.value = arrivalDate
      }

      if (departureField) {
        departureField.value = departureDate
      }

      // Dispatch custom event for other controllers to listen to
      this.element.dispatchEvent(new CustomEvent('datepicker:datesChanged', {
        bubbles: true,
        detail: { arrivalDate, departureDate }
      }))

      // Trigger search form submission after updating fields (unless disabled)
      if (options.triggerSearch !== false) {
        this.triggerSearch()
      }
    }
  }

  triggerSearch() {
    // Check if auto-submit is enabled (defaults to true)
    const autoSubmit = this.data.get("autoSubmit") !== "false"

    if (autoSubmit) {
      // Find the search-form controller and trigger submission
      const searchFormElement = this.element.closest('[data-controller*="search-form"]')
      if (searchFormElement) {
        const searchFormController = this.application.getControllerForElementAndIdentifier(
          searchFormElement,
          "search-form"
        )

        if (searchFormController) {
          searchFormController.submitSearch()
        }
      }
    }
  }

  formatDate(date) {
    // Format as YYYY-MM-DD
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  parseLocalDate(dateStr) {
    // Parse YYYY-MM-DD as local date to avoid UTC vs local time issues
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  isClosedDate(date) {
    if (!this.unavailableDates || this.unavailableDates.length === 0) {
      return false
    }

    // Normalize the input date to compare only dates, not times
    const normalisedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    // Check if this date matches any unavailable date
    return this.unavailableDates.some(closedDate => {
      const normalisedClosed = new Date(closedDate.getFullYear(), closedDate.getMonth(), closedDate.getDate())
      return normalisedDate.getTime() === normalisedClosed.getTime()
    })
  }

  disconnect() {
    if (this.picker) {
      this.picker.destroy()
    }
  }

  // Called automatically by Stimulus when unavailableDates value changes
  unavailableDatesValueChanged() {
    this.parseUnavailableDates()

    // Redraw the calendar to apply new closed date styling
    if (this.picker) {
      this.picker.redraw()
    }
  }

  parseUnavailableDates() {
    let unavailable = {}
    try {
      unavailable = JSON.parse(this.unavailableDatesValue || "{}")
    } catch (e) {
      console.error("Failed to parse unavailable dates JSON:", e)
    }

    // Store unavailable dates as Date objects for comparison
    this.unavailableDates = Object.keys(unavailable).map(dateStr => this.parseLocalDate(dateStr))

    // Keep the raw map (date_str -> reason) so we can show the reason in a tooltip
    this.closedDateReasons = unavailable
  }

  closedDateTooltip(date) {
    const reason = this.closedDateReasons && this.closedDateReasons[this.formatDate(date)]
    const base = "Bookings cannot begin or end on this date."
    return reason ? `${base} Reason: ${reason}` : base
  }

  getMonthsToShow() {
    // Show 1 month on mobile, 2 on desktop
    return window.innerWidth < 768 ? 1 : 2
  }
}
